import { redirect } from 'react-router-dom';

// Constants
import { formOrderNaming } from '../../../../../../components/forms/NewWorkOrderForm/constants';

// GraphQL
import { client } from '../../../../../../gql/client';
import { SAVE_CAMPAIGN } from '../../../../../../gql/mutations/campaign';
import { SAVE_GENERIC_ORDER } from '../../../../../../gql/mutations/generic-order';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import {
  prependCampaignToCache,
  prependGenericOrderToCache,
} from '../../../../../../gql/cacheHelpers';

// Types
import { OrderState } from '../../../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

// Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';

// Redux
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { store } from '../../../../../../state/configureStore';
import { getCursor } from '../../../../../../state/selectors/ui/tables';

export const saveData = async (dataFormRaw: string | null) => {
  const dispatch = store.dispatch;
  if (!dataFormRaw) {
    sessionStorage.removeItem(formOrderNaming.NEW_CAMPAIGN_FORM);
    return redirect('..');
  }

  // Cursors for campaigns
  const cursorCampaignModal = getCursor(tableIds.CAMPAIGN_MODAL)(store.getState());
  const cursorCampaigns = getCursor(tableIds.CAMPAIGNS)(store.getState());

  // Cursors for Work orders
  const cursorWorkOrdersGeneralView = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(store.getState());
  const cursorDuplicateGenericOrder = getCursor(tableIds.DUPLICATE_GENERIC_ORDER)(store.getState());
  const cursorSearchAndCreateWorkOrders = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS)(store.getState());
  const cursorGenericModal = getCursor(tableIds.GENERIC_ORDER_MODAL)(store.getState());

  const creationDate = new Date().toISOString();

  const dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));
  const pkInitArray = dataForm.pkInit.replace(/ /g, '').split('+');
  const pkEndArray = dataForm.pkEnd.replace(/ /g, '').split('+');

  // Bloque C-2/C-3: SAVE_GENERIC_ORDER y SAVE_CAMPAIGN sustituyen la cascada
  // legacy CREATE_NEW_ORDER+CREATE_NEW_<entity>+UPDATE_ORDER+UPDATE_ORDER_RELATIONSHIPS
  // por una mutación atómica vía $transaction Prisma.
  let createdOrder;

  if (dataForm.campaign) {
    // Rama Campaign — Bloque C-3
    const result = await client.mutate({
      mutation: SAVE_CAMPAIGN,
      variables: {
        input: {
          // Idempotencia online: UUID estable por operación (RetryLink no duplica).
          clientOpId: crypto.randomUUID(),
          state: OrderState.Open,
          creationDate,
          registerInitDate: dataForm.registerInitDate,
          pkInitKm: Number(pkInitArray[0]),
          pkInitMeter: Number(pkInitArray[1]),
          pkEndKm: Number(pkEndArray[0]),
          pkEndMeter: Number(pkEndArray[1]),
          track: dataForm.track,
          direction: dataForm?.direction,
          margin: dataForm?.margin,
          lane: dataForm.lane,
          concessionId: dataForm.concession,
          roadId: dataForm.road,
          teamIds: Array.isArray(dataForm.teamRelationships)
            ? dataForm.teamRelationships
            : dataForm.teamRelationships
              ? String(dataForm.teamRelationships).split(',').map((s: string) => s.trim()).filter(Boolean)
              : [],
          capitolId: dataForm.capitol,
          subCapitolId: dataForm.subCapitol,
          classification: dataForm.classification,
          ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
          description: dataForm.description,
          name: dataForm.campaign,
        },
      },
      update(cache, { data: { saveCampaign } }) {
        prependCampaignToCache(cache, saveCampaign.campaign);
        if (cursorCampaigns?.cursor !== undefined) {
          dispatch(setCursor(tableIds.CAMPAIGN_MODAL, {
            ...cursorCampaignModal, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.CAMPAIGNS, {
            ...cursorCampaigns, take: takeBase, cursor: '', orderBy: 'desc',
          }));
        }
      },
    });
    createdOrder = result.data?.saveCampaign?.order;
  } else {
    // Rama Generic — Bloque C-2 (sin nombre de campaña → entidad Generic)
    const result = await client.mutate({
      mutation: SAVE_GENERIC_ORDER,
      variables: {
        input: {
          // Idempotencia: congela el opId en las variables → RetryLink/offline
          // reintentan con el mismo id y el backend deduplica (no crea 2ª OT).
          clientOpId: crypto.randomUUID(),
          state: OrderState.Open,
          creationDate,
          registerInitDate: dataForm.registerInitDate,
          pkInitKm: Number(pkInitArray[0]),
          pkInitMeter: Number(pkInitArray[1]),
          pkEndKm: Number(pkEndArray[0]),
          pkEndMeter: Number(pkEndArray[1]),
          track: dataForm.track,
          direction: dataForm?.direction,
          margin: dataForm?.margin,
          lane: dataForm.lane,
          concessionId: dataForm.concession,
          roadId: dataForm.road,
          teamIds: Array.isArray(dataForm.teamRelationships)
            ? dataForm.teamRelationships
            : dataForm.teamRelationships
              ? String(dataForm.teamRelationships).split(',').map((s: string) => s.trim()).filter(Boolean)
              : [],
          capitolId: dataForm.capitol,
          subCapitolId: dataForm.subCapitol,
          classification: dataForm.classification,
          ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
          description: dataForm.description,
        },
      },
      update(cache, { data: { saveGenericOrder } }) {
        prependGenericOrderToCache(cache, saveGenericOrder.genericOrder);
        if (cursorWorkOrdersGeneralView?.cursor !== undefined) {
          dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, {
            ...cursorWorkOrdersGeneralView, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, {
            ...cursorDuplicateGenericOrder, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL, {
            ...cursorGenericModal, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, {
            ...cursorSearchAndCreateWorkOrders, take: takeBase, cursor: '', orderBy: 'desc',
          }));
        }
      },
    });
    createdOrder = result.data?.saveGenericOrder?.order;
  }

  // Refetch
  if (createdOrder?.id) {
    await client.query({
      fetchPolicy: 'network-only',
      query: GET_ORDER_BY_ID,
      variables: { id: createdOrder.id },
    });
  }

  // Clean the form local storage as it was just saved
  sessionStorage.removeItem(formOrderNaming.NEW_CAMPAIGN_FORM);
  return redirect(`../${createdOrder?.id ?? ''}`);
};
